import { StoreService } from '../../../service/store/store.service';
import { CartPopupService } from '../../../service/cart/cart-popup.service';
import { paletteSku } from '../../../data/product/product-lookup.data';
import { ChangeDetectionStrategy, Component, ElementRef, Input, NgZone, OnInit } from '@angular/core';
import { isPaletteShown, THEME_PALETTES } from '../../../data/palette/palette.data';
import { URLS } from '../../../data/navigation/navigation.data';
import { THEMES } from '../../../data/product/product.data';
import { FlatPlatform } from '../../../model/product/flat-platform.model';
import { PalettePlatform, ThemePalette } from '../../../model/palette/palette.model';
import { isThemeFree, Product } from '../../../model/product/product.model';
import { AnalyticsService } from '../../../service/analytics/analytics.service';
import { RoutingService } from '../../../service/routing/routing.service';
import { ShowcaseTheme, ThemeShowcaseComponent } from '../theme-showcase/theme-showcase.component';

/**
 * The home's palettes box (owner 2026-10-11: "paletleri themes gibi kutuya"): the themes box's panel,
 * colours, docks and lightbox (it extends ThemeShowcaseComponent and wears its stylesheet), one palette
 * at a time. Left: the palette's name, its 24 named colors as a strip, its page and its Get Now. Right:
 * the palette in the chosen app (Figma, CorelDRAW, the Mac's color picker), the apps as logos; under
 * both, every palette as its theme's icon. Unlike the themes box it keeps its own state.
 */
@Component( {
  selector: 'app-palette-showcase',
  templateUrl: './palette-showcase.component.html',
  styleUrls: [
    '../product-showcase/product-showcase.component.scss',
    '../theme-showcase/theme-showcase.component.scss',
    './palette-showcase.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.Default,
} )
export class PaletteShowcaseComponent extends ThemeShowcaseComponent implements OnInit {
  /** the site in dark mode (the box takes the theme's colours as the themes box does) */
  @Input() override siteDark = false;

  private readonly byKey = new Map<string, { palette: ThemePalette; theme: ShowcaseTheme }>();

  constructor( host: ElementRef<HTMLElement>, zone: NgZone,
               private routingService: RoutingService, private analyticsService: AnalyticsService,
               private storeService: StoreService, private cartPopupService: CartPopupService ) {
    super( host, zone );
  }

  /** One palette alone (the palettes page's expander, owner 2026-10-15): no palettes' dock, that palette
   *  and its apps only, opened on `app` (an app's name) when the page filters by one. */
  @Input() set only( entry: { product: Product; category: string; palette: ThemePalette | null; app?: string } | null ) {
    if ( !entry?.palette ) {
      this.single = false;
      return;
    }
    this.byKey.clear();
    this.byKey.set( entry.product.key, { palette: entry.palette, theme: { product: entry.product, category: entry.category } } );
    this.themes = [];
    // the opening pick is not the visitor's: no Palette Platform Selected for it
    this.single = false;
    this.pickPalette( entry.product.key );
    // the page's app filter opens the box on that app
    const filtered = entry.palette.platforms.findIndex( pl => pl.name === entry.app );
    if ( filtered >= 0 ) {
      this.pickApp( filtered );
    }
    this.single = true;
  }

  /** whether the box shows one palette alone (the palettes page's expander) */
  single = false;

  /** where the box's events come from (the home's box or the palettes page's expander) */
  private get source(): string {
    return this.single ? 'palette-expander' : 'home-palettes';
  }

  ngOnInit(): void {
    if ( this.single ) {
      return;
    }
    // the palettes in the dock: only the ones it can show (owner 2026-10-15: a palette with no pictures yet
    // is left out, no longer greyed out)
    const items: ShowcaseTheme[] = [];
    for ( const line of THEMES ) {
      for ( const product of line.products || [] ) {
        const palette = THEME_PALETTES.find( pl => pl.key === product.key );
        if ( palette && isPaletteShown( palette.key ) ) {
          const theme = { product, category: line.name };
          items.push( theme );
          this.byKey.set( palette.key, { palette, theme } );
        }
      }
    }
    this.themes = items;
    // opens on a palette picked at random
    const open = [ ...this.byKey.keys() ];
    if ( open.length ) {
      this.pickPalette( open[ Math.floor( Math.random() * open.length ) ] );
    }
  }

  get palette(): ThemePalette | null {
    return this.product ? this.byKey.get( this.product.key )?.palette ?? null : null;
  }

  get paletteApp(): PalettePlatform | null {
    return this.palette?.platforms[ this.platformIndex ] ?? null;
  }

  /** picking a palette keeps the app shown when the new one has it */
  pickPalette( key: string ): void {
    const entry = this.byKey.get( key );
    if ( !entry ) {
      return;
    }
    const keep = this.paletteApp?.key;
    this.active = entry.theme;
    this.platforms = entry.palette.platforms as unknown as FlatPlatform[];
    const at = entry.palette.platforms.findIndex( pl => pl.key === keep );
    this.pickApp( at >= 0 ? at : entry.palette.platforms.length - 1 );
  }

  pickApp( index: number ): void {
    if ( this.single && this.platformIndex !== index && this.palette?.platforms[ index ] ) {
      this.analyticsService.logEvent( 'Palette Platform Selected', {
        palette: this.product?.name,
        key: this.product?.key,
        platform: this.palette.platforms[ index ].key,
        source: 'expander',
      } );
    }
    this.platformIndex = index;
    this.screenshot = this.paletteApp?.screenshot ? { src: this.paletteApp.screenshot.src } : null;
  }

  override titleOf( p: Product | null | undefined ): string {
    return p ? `${ p.name } Palette` : '';
  }

  /** premium themes show their palette in full, but it cannot be bought yet: its button reads Coming Soon (owner 2026-10-11) */
  get premium(): boolean {
    return !!this.product && !isThemeFree( this.product );
  }

  /** a premium palette is sold once the store prices it (2026-10-15, $0.99); until then it reads Coming Soon */
  get comingSoon(): boolean {
    return this.premium && this.storeService.availability( this.paletteSkuOnShow ) !== 'buy';
  }

  get firstAndLast(): string {
    const colors = this.palette?.colors || [];
    return colors.length ? `from ${ colors[ 0 ].name } to ${ colors[ colors.length - 1 ].name }` : '';
  }

  override get platformLine(): string {
    return PaletteShowcaseComponent.APP_LINES[ this.paletteApp?.key || '' ] || '';
  }

  private static readonly APP_LINES: Record<string, string> = {
    figma: 'Design tokens for Figma variables, ready for Tokens Studio',
    coreldraw: 'A custom palette for the CorelDRAW palette docker',
    macos: 'A color list for the color picker of every app on your Mac',
  };

  /** an app's short name in the dock ("Figma", "CorelDRAW", "macOS") */
  appShort( pl: PalettePlatform ): string {
    return pl.key === 'macos' ? 'macOS' : pl.name.replace( / Palette$/, '' );
  }

  /** the app's name without "Palette" ("Figma", "CorelDRAW", "MacOS Color Picker") */
  get appName(): string {
    const name = this.paletteApp?.name || '';
    return this.paletteApp?.key === 'macos' ? 'the MacOS Color Picker' : name.replace( / Palette$/, '' );
  }

  readonly paletteUrl = URLS.colorPalette;

  get paletteQuery(): Record<string, string> {
    return { product: this.product?.key || '', category: ( this.active?.category || '' ).toLowerCase() };
  }

  /** Add to cart (owner 2026-10-12: a free palette goes through the cart like a theme), for the app on show */
  getNow(): void {
    const sku = this.paletteSkuOnShow;
    if ( this.comingSoon || !this.storeService.canAdd( sku ) ) {
      return;
    }
    this.cartPopupService.add( sku, this.source );
  }

  /** Discover More: the palettes page's expander records it, as its old View full details did */
  discover(): void {
    if ( this.single ) {
      this.analyticsService.logEvent( 'Palette Clicked', { palette: this.product?.name, key: this.product?.key, source: 'expander' } );
    }
  }

  /** the store sku of the palette and app on show ('aurora-borealis/figma-palette') */
  get paletteSkuOnShow(): string {
    return this.product && this.paletteApp ? paletteSku( this.product.key, this.paletteApp.key ) : '';
  }

  /** the price of the palette and app on show ("Free", "$1.99"), or null */
  get appPrice(): string | null {
    return this.paletteSkuOnShow ? this.storeService.priceLabel( this.paletteSkuOnShow ) : null;
  }

  get cartDisabled(): boolean {
    return this.comingSoon || !this.storeService.canAdd( this.paletteSkuOnShow );
  }

  /** the hex just copied from the strip, shown as "Copied" in its label for a moment */
  copiedHex = '';
  private copiedTimer?: ReturnType<typeof setTimeout>;

  copyHex( hex: string ): void {
    navigator.clipboard?.writeText( hex ).catch( () => undefined );
    this.copiedHex = hex;
    clearTimeout( this.copiedTimer );
    this.copiedTimer = setTimeout( () => ( this.copiedHex = '' ), 1200 );
  }

  /** the picture enlarged: the lightbox walks this palette's apps */
  override enlarge(): void {
    const name = this.title;
    this.shots = ( this.palette?.platforms || [] ).filter( pl => pl.screenshot?.src ).map( pl => ( {
      name: `${ name } in ${ pl.name.replace( / Palette$/, '' ) }`,
      src: pl.screenshot!.src, large: pl.screenshot!.src, alt: pl.screenshot!.description,
    } ) );
    const current = this.shots.find( s => s.src === this.screenshot?.src ) || this.shots[ 0 ];
    if ( current ) {
      this.openLightbox( current );
    }
  }
}
